const STORAGE_KEY = 'sb:pendingPaymentSetupAction';
const TTL_MS = 30 * 60 * 1000;

export type PendingReduxActionType = 'updateAddons' | 'addPromotionCode' | 'restartSubscription';

/**
 * Intent persisted across the Stripe/PayPal payment-method-setup redirect so
 * the originally requested purchase can resume once setup succeeds.
 *
 * - `redux-action`: resumed by re-dispatching a registered action (see the
 *   creator registry in PaymentMethodSetupRequiredModal).
 * - `update-addons-modal`: reopens UpdateAddonsModal with the user's previous
 *   selection and auto-submits it, so its 3DS/polling/redirect flow runs as
 *   if the setup detour never happened.
 */
export type PendingPaymentSetupAction =
  | {
      kind: 'redux-action';
      actionType: PendingReduxActionType;
      payload: unknown;
    }
  | {
      kind: 'update-addons-modal';
      modalData: { addon: string; templateId?: string };
      formState: {
        billingCycle: string;
        quantityValue: string;
        videoPlan: string;
        selectedTemplate: string;
      };
    };

type StoredAction = PendingPaymentSetupAction & { createdAt: number };

export const savePendingPaymentSetupAction = (action: PendingPaymentSetupAction): void => {
  try {
    const stored: StoredAction = { ...action, createdAt: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch (e) {
    console.error('Failed to save pending payment setup action', e);
  }
};

export const clearPendingPaymentSetupAction = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear pending payment setup action', e);
  }
};

/**
 * Reads and clears the stored action (one-shot, so a resume can never fire
 * twice). Returns null when nothing is stored or the entry expired.
 */
export const takePendingPaymentSetupAction = (): PendingPaymentSetupAction | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw) as StoredAction;
    if (!stored.createdAt || Date.now() - stored.createdAt > TTL_MS) return null;

    return stored;
  } catch (e) {
    console.error('Failed to read pending payment setup action', e);

    return null;
  }
};
